import { STORAGE_KEYS, ERROR_MESSAGES } from '../utils/constants';

/**
 * Base API Service
 * Handles all HTTP requests with automatic token attachment and error handling
 */
class ApiService {
  constructor() {
    this.baseURL =
      process.env.REACT_APP_API_BASE_URL ||
      "https://cititekpro-com.onrender.com";
  }

  /**
   * Get auth token from localStorage
   * @returns {string|null} - JWT token
   */
  getToken() {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Build request headers with optional auth token
   * @param {boolean} includeAuth - Whether to include auth token
   * @returns {object} - Headers object
   */
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Handle API errors
   * @param {Response} response - Fetch response
   * @returns {Promise} - Rejected promise with error
   */
  async handleError(response) {
    let errorMessage;
    let backendErrorMessage = null;

    try {
      const errorData = await response.json();
      backendErrorMessage = errorData.error || errorData.message;
      errorMessage = backendErrorMessage || ERROR_MESSAGES.SERVER_ERROR;
    } catch {
      errorMessage = ERROR_MESSAGES.SERVER_ERROR;
    }

    // Handle specific status codes
    if (response.status === 401) {
      // Token expired or unauthorized
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);

      // Only redirect if not already on login/register pages
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }

      // Only use generic message if backend didn't provide a specific one
      if (!backendErrorMessage) {
        errorMessage = ERROR_MESSAGES.UNAUTHORIZED;
      }
    } else if (response.status === 403) {
      // Only use generic message if backend didn't provide a specific one
      if (!backendErrorMessage) {
        errorMessage = ERROR_MESSAGES.FORBIDDEN;
      }
    } else if (response.status === 404) {
      // Only use generic message if backend didn't provide a specific one
      if (!backendErrorMessage) {
        errorMessage = ERROR_MESSAGES.NOT_FOUND;
      }
    } else if (response.status === 423) {
      // Account locked - requires admin intervention
      // Use the backend message which includes instructions for the user
      if (!backendErrorMessage) {
        errorMessage = ERROR_MESSAGES.ACCOUNT_LOCKED ||
          'Your account has been locked due to suspicious activity. Please contact an administrator.';
      }
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    error.isAccountLocked = response.status === 423;
    throw error;
  }

  /**
   * Make HTTP request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const includeAuth = options.includeAuth !== false;

    const config = {
      ...options,
      headers: {
        ...this.getHeaders(includeAuth),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        return await this.handleError(response);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        // Return the data portion of the response or the entire response
        return data.data || data;
      }

      return response;
    } catch (error) {
      // Network errors or other exceptions
      if (error.message === 'Failed to fetch' || error.name === 'NetworkError') {
        const networkError = new Error(ERROR_MESSAGES.NETWORK_ERROR);
        throw networkError;
      }
      throw error;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
